import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Plus, Pencil, Trash2, X, PackagePlus, AlertTriangle, ShoppingCart,
  TrendingDown, QrCode, DollarSign, RefreshCw, CheckCircle2, Boxes,
  ScanLine, BarChart3,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${(Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = "w-full min-w-0 box-border rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";
const BLANK = { name: "", sku: "", barcode: "", supplier: "", reorder_email: "", cost: "", price: "", tax_pct: "", qty: "", reorder_threshold: "" };

function Stat({ label, value, testid, tone }) {
  return (
    <div data-testid={testid} className={`rounded-xl border px-4 py-3 ${tone === "warn" ? "border-[#c9a961]/50 bg-[#c9a961]/10" : "border-[#1e3d3a]/10 bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">{label}</div>
      <div className="text-lg font-semibold text-[#1e3d3a] tabular-nums">{value}</div>
    </div>
  );
}

export default function InventoryBoard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemForm, setItemForm] = useState(null);
  const [savingItem, setSavingItem] = useState(false);

  // Register state
  const [saleItemId, setSaleItemId] = useState("");
  const [saleQty, setSaleQty] = useState("1");
  const [salePrice, setSalePrice] = useState("");
  const [ringing, setRinging] = useState(false);
  const [checkout, setCheckout] = useState(null); // {sale, url}
  const pollRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanMode, setScanMode] = useState("register");
  const [restockTarget, setRestockTarget] = useState(null); // {item, qty}
  const [restocking, setRestocking] = useState(false);
  const scannerRef = useRef(null);
  const [report, setReport] = useState(null);
  const [reportRange, setReportRange] = useState("week");
  const [reportLoading, setReportLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/portal/inventory"); setData(data); }
    catch { toast.error("Couldn't load inventory."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    loadReport("week");
    const t = setInterval(load, 6000); // live feed
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const items = data?.items || [];
  const selected = useMemo(() => items.find((i) => i.id === saleItemId), [items, saleItemId]);
  useEffect(() => { if (selected) setSalePrice(String(selected.price)); }, [saleItemId]); // eslint-disable-line

  const saveItem = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    try {
      if (itemForm.id) await api.patch(`/portal/inventory/items/${itemForm.id}`, itemForm);
      else await api.post("/portal/inventory/items", itemForm);
      toast.success(itemForm.id ? "Item updated." : "Item added.");
      setItemForm(null); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Couldn't save the item."); }
    finally { setSavingItem(false); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/portal/inventory/items/${id}`); toast.success("Item removed."); load();
  };

  const restock = async (item) => {
    const add = window.prompt(`Add how many units to "${item.name}"? (current: ${item.qty})`);
    if (add === null) return;
    await api.patch(`/portal/inventory/items/${item.id}`, { restock: Number(add) || 0 });
    toast.success("Stock updated."); load();
  };

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadReport = async (range) => {
    setReportRange(range); setReportLoading(true);
    try { const { data } = await api.get(`/portal/inventory/report?range=${range}`); setReport(data); }
    catch { toast.error("Couldn't load the report."); }
    finally { setReportLoading(false); }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const onScan = (code) => {
    const val = (code || "").trim();
    if (!val) return;
    if (scanMode === "form") {
      // Capture the barcode straight into the item being added/edited.
      setItemForm((prev) => ({ ...(prev || { ...BLANK }), barcode: val }));
      toast.success(`Barcode captured: ${val}`);
      stopScanner();
      return;
    }
    if (scanMode === "restock") {
      const hit = items.find((i) => (i.barcode || "").trim() === val);
      stopScanner();
      if (hit) { setRestockTarget({ item: hit, qty: 1 }); }
      else { setItemForm({ ...BLANK, barcode: val }); toast(`No item with barcode ${val} — add it now.`, { icon: "🆕" }); }
      return;
    }
    // Register lookup.
    const hit = items.find((i) => (i.barcode || "").trim() && i.barcode.trim() === val);
    if (hit) {
      setSaleItemId(hit.id);
      toast.success(`Scanned: ${hit.name}`);
      stopScanner();
    } else {
      // No match — offer to create a new item pre-filled with this barcode.
      stopScanner();
      setItemForm({ ...BLANK, barcode: val });
      toast(`No item with barcode ${val} — add it now.`, { icon: "🆕" });
    }
  };

  const openScanner = (mode) => { setScanMode(mode); setScanning(true); };

  const doRestock = async () => {
    if (!restockTarget) return;
    const add = Math.round(Number(restockTarget.qty) || 0);
    if (add <= 0) { toast.error("Enter how many to add."); return; }
    setRestocking(true);
    try {
      await api.patch(`/portal/inventory/items/${restockTarget.item.id}`, { restock: add });
      toast.success(`Added ${add} to ${restockTarget.item.name}.`);
      setRestockTarget(null);
      load();
    } catch { toast.error("Couldn't restock."); }
    finally { setRestocking(false); }
  };

  useEffect(() => {
    if (!scanning) return;
    const el = "inv-scanner-region";
    const h = new Html5Qrcode(el);
    scannerRef.current = h;
    h.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 160 } },
      (decoded) => onScan(decoded), () => {})
      .catch(() => { toast.error("Couldn't start the camera. Check permissions."); setScanning(false); });
    return () => { h.stop().then(() => h.clear()).catch(() => {}); };
    // eslint-disable-next-line
  }, [scanning]);

  const ringUp = async (method) => {
    if (!selected) { toast.error("Pick an item first."); return; }
    setRinging(true);
    let win = null;
    if (method === "stripe") win = window.open("", "_blank"); // open synchronously to survive popup blockers
    try {
      const body = { item_id: selected.id, qty: Number(saleQty) || 1, unit_price: Number(salePrice) || selected.price, method, origin_url: window.location.origin };
      const { data: res } = await api.post("/portal/inventory/checkout", body);
      if (method === "stripe") {
        if (win) win.location = res.checkout_url;
        setCheckout({ sale: res.sale, url: res.checkout_url });
        // poll for payment
        stopPoll();
        pollRef.current = setInterval(async () => {
          try {
            const { data: s } = await api.get(`/portal/inventory/sales/${res.sale.id}`);
            if (s.status === "paid") { stopPoll(); setCheckout((c) => c && { ...c, paid: true }); toast.success("Payment received!"); load(); }
          } catch { /* keep polling */ }
        }, 3000);
      } else {
        toast.success(`Sold ${body.qty} × ${selected.name} — ${money(res.sale.total)}`);
        setSaleQty("1"); load();
      }
    } catch (e) {
      if (win) win.close();
      toast.error(e?.response?.data?.detail || "Couldn't complete the sale.");
    } finally { setRinging(false); }
  };

  const closeCheckout = () => { stopPoll(); setCheckout(null); setSaleQty("1"); load(); };

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#c9a961]" /></div>;

  const st = data?.stats || {};
  const lowStock = data?.low_stock || [];
  const slow = data?.slow_movers || [];
  const recent = data?.recent_sales || [];
  const previewTotal = selected ? (Number(salePrice) || selected.price) * (Number(saleQty) || 1) : 0;

  return (
    <div data-testid="inventory-board" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="inv-stat-today" label="Sold today" value={`${st.sales_today_count || 0} · ${money(st.sales_today_total)}`} />
        <Stat testid="inv-stat-week" label="This week" value={`${st.sales_week_count || 0} · ${money(st.sales_week_total)}`} />
        <Stat testid="inv-stat-value" label="Stock value (cost)" value={money(st.inventory_value)} />
        <Stat testid="inv-stat-low" label="Low stock" value={st.low_stock_count || 0} tone={st.low_stock_count ? "warn" : undefined} />
      </div>

      {lowStock.length > 0 && (
        <div data-testid="inv-low-banner" className="flex items-start gap-3 rounded-2xl border border-[#c9a961]/50 bg-[#c9a961]/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-[#a3823f] shrink-0 mt-0.5" />
          <div className="text-[13px] text-[#1e3d3a]/85">
            <span className="font-semibold">Reorder now — {lowStock.length} item{lowStock.length > 1 ? "s" : ""} low.</span>{" "}
            {lowStock.map((i) => `${i.name} (${i.qty} left)`).join(", ")}.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Register */}
        <div className="rounded-2xl border border-[#1e3d3a]/10 bg-white p-5">
          <div className="flex items-center gap-2 mb-3"><ShoppingCart className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg text-[#1e3d3a]">Register</h3></div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50">Item</label>
            <button data-testid="inv-scan-btn" type="button" onClick={() => openScanner("register")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#a3823f] hover:underline"><ScanLine className="h-3.5 w-3.5" /> Scan barcode</button>
          </div>
          <select data-testid="inv-sale-item" value={saleItemId} onChange={(e) => setSaleItemId(e.target.value)} className={inputCls}>
            <option value="">Look up an item…</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.name} — {money(i.price)} ({i.qty} in stock)</option>)}
          </select>
          {selected && (
            <>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Quantity</label>
                  <input data-testid="inv-sale-qty" type="number" min="1" value={saleQty} onChange={(e) => setSaleQty(e.target.value)} className={inputCls} />
                </div>
                <div className="min-w-0">
                  <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Price each (tax incl.)</label>
                  <input data-testid="inv-sale-price" type="number" step="0.01" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-[#1e3d3a]/5 px-4 py-2.5">
                <span className="text-[11px] uppercase tracking-wide text-[#1e3d3a]/55">Total</span>
                <span data-testid="inv-sale-total" className="text-xl font-semibold text-[#1e3d3a] tabular-nums">{money(previewTotal)}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid="inv-ring-cash" disabled={ringing} onClick={() => ringUp("manual_cash")} className="flex-1 min-w-[110px] inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50"><DollarSign className="h-4 w-4" /> Cash</button>
                <button data-testid="inv-ring-card" disabled={ringing} onClick={() => ringUp("manual_card")} className="flex-1 min-w-[110px] inline-flex items-center justify-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-4 py-2.5 text-sm font-semibold text-[#1e3d3a] hover:bg-[#1e3d3a]/5 disabled:opacity-50">Card (in person)</button>
                <button data-testid="inv-ring-stripe" disabled={ringing} onClick={() => ringUp("stripe")} className="flex-1 min-w-[110px] inline-flex items-center justify-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2.5 text-sm font-semibold text-[#1e3d3a] hover:bg-[#b8975a] disabled:opacity-50">{ringing ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Card / QR</button>
              </div>
              <p className="mt-2 text-[11px] text-[#1e3d3a]/45">Card/QR = customer pays by card, Apple Pay or Google Pay (Stripe test mode). Stock drops automatically when a sale is paid.</p>
            </>
          )}
        </div>

        {/* Live sales feed */}
        <div className="rounded-2xl border border-[#1e3d3a]/10 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg text-[#1e3d3a]">Live sales</h3></div>
            <span className="text-[10px] uppercase tracking-wide text-[#2f7d5b]">● Live</span>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-[#1e3d3a]/45 py-6 text-center">No sales yet. Ring one up on the register.</p>
          ) : (
            <ul data-testid="inv-sales-feed" className="space-y-1.5 max-h-72 overflow-auto">
              {recent.map((s) => (
                <li key={s.id} className="flex items-center justify-between text-[13px] border-b border-[#1e3d3a]/6 py-1.5">
                  <span className="text-[#1e3d3a]/80">{s.qty} × {s.item_name} <span className="text-[10px] uppercase text-[#1e3d3a]/40">{(s.method || "").replace("manual_", "")}</span></span>
                  <span className="tabular-nums font-semibold text-[#1e3d3a]">{money(s.total)}</span>
                </li>
              ))}
            </ul>
          )}
          {slow.length > 0 && (
            <div className="mt-4 rounded-xl bg-[#1e3d3a]/5 px-3 py-2.5" data-testid="inv-slow-movers">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#1e3d3a]/55 mb-1"><TrendingDown className="h-3.5 w-3.5" /> Not selling (14 days)</div>
              <div className="text-[12px] text-[#1e3d3a]/70">{slow.map((i) => i.name).join(", ")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Items table */}
      <div className="rounded-2xl border border-[#1e3d3a]/10 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Boxes className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg text-[#1e3d3a]">Inventory</h3></div>
          <div className="flex items-center gap-2">
            <button data-testid="inv-scan-restock" onClick={() => openScanner("restock")} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 px-3.5 py-1.5 text-xs font-semibold text-[#1e3d3a] hover:bg-[#c9a961]/10"><ScanLine className="h-3.5 w-3.5" /> Scan to restock</button>
            <button data-testid="inv-add-item" onClick={() => setItemForm({ ...BLANK })} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d]"><Plus className="h-3.5 w-3.5" /> Add item</button>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-[#1e3d3a]/45 py-6 text-center">No items yet. Add your first product to start selling.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10">
                <th className="py-2">Item</th><th className="py-2 text-right">Cost</th><th className="py-2 text-right">Price</th><th className="py-2 text-right">Tax</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} data-testid={`inv-row-${i.id}`} className="border-b border-[#1e3d3a]/6">
                    <td className="py-2 text-[#1e3d3a]/85">{i.name}
                      {i.sku && <span className="ml-1.5 text-[10px] text-[#1e3d3a]/40">{i.sku}</span>}
                      {i.low_stock && <span className="ml-1.5 rounded-full bg-[#a3823f]/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#a3823f]">Reorder</span>}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[#1e3d3a]/60">{money(i.cost)}</td>
                    <td className="py-2 text-right tabular-nums text-[#1e3d3a]">{money(i.price)}</td>
                    <td className="py-2 text-right tabular-nums text-[#1e3d3a]/50">{i.tax_pct ? `${i.tax_pct}%` : "—"}</td>
                    <td className={`py-2 text-right tabular-nums font-semibold ${i.low_stock ? "text-[#a3823f]" : "text-[#1e3d3a]"}`}>{i.qty}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button data-testid={`inv-restock-${i.id}`} title="Restock" onClick={() => restock(i)} className="p-1 text-[#1e3d3a]/40 hover:text-[#2f7d5b]"><PackagePlus className="h-4 w-4" /></button>
                      <button data-testid={`inv-edit-${i.id}`} title="Edit" onClick={() => setItemForm({ ...i, cost: String(i.cost), price: String(i.price), tax_pct: String(i.tax_pct), qty: String(i.qty), reorder_threshold: String(i.reorder_threshold) })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`inv-del-${i.id}`} title="Delete" onClick={() => delItem(i.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sales report */}
      <div className="rounded-2xl border border-[#1e3d3a]/10 bg-white p-5" data-testid="inv-report">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg text-[#1e3d3a]">Sales report</h3></div>
          <div className="inline-flex rounded-full border border-[#1e3d3a]/12 p-0.5">
            {["today", "week", "month"].map((r) => (
              <button key={r} data-testid={`inv-report-${r}`} onClick={() => loadReport(r)}
                className={`px-3 py-1 text-[11px] uppercase tracking-wide rounded-full ${reportRange === r ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a]"}`}>{r}</button>
            ))}
          </div>
        </div>
        {reportLoading ? (
          <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline text-[#c9a961]" /></div>
        ) : !report || report.rows.length === 0 ? (
          <p className="text-sm text-[#1e3d3a]/45 py-6 text-center">No paid sales in this period yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat testid="inv-report-orders" label="Orders" value={report.totals.orders} />
              <Stat testid="inv-report-units" label="Units" value={report.totals.units} />
              <Stat testid="inv-report-revenue" label="Revenue" value={money(report.totals.revenue)} />
              <Stat testid="inv-report-profit" label="Profit" value={money(report.totals.profit)} tone={report.totals.profit >= 0 ? undefined : "warn"} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10">
                  <th className="py-2">Best sellers</th><th className="py-2 text-right">Units</th><th className="py-2 text-right">Revenue</th><th className="py-2 text-right">Cost</th><th className="py-2 text-right">Profit</th>
                </tr></thead>
                <tbody>
                  {report.rows.map((r, idx) => (
                    <tr key={r.item_id} className="border-b border-[#1e3d3a]/6">
                      <td className="py-2 text-[#1e3d3a]/85">{idx === 0 && <span className="mr-1 text-[#c9a961]">★</span>}{r.name}</td>
                      <td className="py-2 text-right tabular-nums text-[#1e3d3a]/70">{r.units}</td>
                      <td className="py-2 text-right tabular-nums text-[#1e3d3a]">{money(r.revenue)}</td>
                      <td className="py-2 text-right tabular-nums text-[#1e3d3a]/50">{money(r.cost)}</td>
                      <td className="py-2 text-right tabular-nums font-semibold text-[#2f7d5b]">{money(r.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Quick restock mini-modal (after a scan) */}
      {restockTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="inv-restock-modal">
          <div className="w-full max-w-xs rounded-2xl bg-white p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head text-lg text-[#1e3d3a]">Restock</h3>
              <button onClick={() => setRestockTarget(null)} className="p-1.5 rounded-full hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-[13px] text-[#1e3d3a]/70">{restockTarget.item.name}{restockTarget.item.sku ? ` · ${restockTarget.item.sku}` : ""}</p>
            <p className="text-[12px] text-[#1e3d3a]/45 mb-3">On hand: {restockTarget.item.qty}</p>
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Add units</label>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setRestockTarget((t) => ({ ...t, qty: Math.max(1, (Number(t.qty) || 1) - 1) }))} className="h-9 w-9 rounded-lg border border-[#1e3d3a]/15 text-lg text-[#1e3d3a] hover:bg-[#1e3d3a]/5">−</button>
              <input data-testid="inv-restock-qty" type="number" min="1" value={restockTarget.qty} onChange={(e) => setRestockTarget((t) => ({ ...t, qty: e.target.value }))} className={inputCls + " text-center"} />
              <button type="button" onClick={() => setRestockTarget((t) => ({ ...t, qty: (Number(t.qty) || 0) + 1 }))} className="h-9 w-9 rounded-lg border border-[#1e3d3a]/15 text-lg text-[#1e3d3a] hover:bg-[#1e3d3a]/5">+</button>
            </div>
            <p className="mt-2 text-[12px] text-[#1e3d3a]/55">New on hand: <span className="font-semibold text-[#1e3d3a]">{restockTarget.item.qty + (Number(restockTarget.qty) || 0)}</span></p>
            <div className="mt-4 flex gap-2">
              <button data-testid="inv-restock-confirm" onClick={doRestock} disabled={restocking} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{restocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />} Add stock</button>
              <button data-testid="inv-restock-again" onClick={() => { setRestockTarget(null); openScanner("restock"); }} className="rounded-full border border-[#1e3d3a]/15 px-4 py-2.5 text-sm font-semibold text-[#1e3d3a] hover:bg-[#c9a961]/10">Scan next</button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode scanner modal */}
      {scanning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="inv-scanner-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 text-center">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head text-lg text-[#1e3d3a]">Scan a barcode</h3>
              <button onClick={stopScanner} className="p-1.5 rounded-full hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            <div id="inv-scanner-region" className="rounded-xl overflow-hidden bg-black/5 min-h-[200px]" />
            <p className="mt-3 text-[12px] text-[#1e3d3a]/55">Point the camera at the item's barcode. Add the barcode to an item first so it can be matched.</p>
          </div>
        </div>
      )}

      {/* Item add/edit modal */}
      {itemForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="inv-item-modal">
          <form onSubmit={saveItem} className="w-full max-w-md rounded-2xl bg-white p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl text-[#1e3d3a]">{itemForm.id ? "Edit item" : "Add item"}</h3>
              <button type="button" onClick={() => setItemForm(null)} className="p-1.5 rounded-full hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Name</label><input data-testid="inv-form-name" required value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">SKU</label><input value={itemForm.sku} onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Supplier</label><input value={itemForm.supplier} onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Barcode / UPC</label>
                <div className="flex gap-2">
                  <input data-testid="inv-form-barcode" value={itemForm.barcode} onChange={(e) => setItemForm({ ...itemForm, barcode: e.target.value })} placeholder="Scan or type" className={inputCls} />
                  <button data-testid="inv-form-scan" type="button" onClick={() => openScanner("form")} title="Scan with camera" className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-[#1e3d3a]/15 px-2.5 text-[#a3823f] hover:bg-[#c9a961]/10"><ScanLine className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Reorder alert email</label><input data-testid="inv-form-reorder-email" type="email" value={itemForm.reorder_email} onChange={(e) => setItemForm({ ...itemForm, reorder_email: e.target.value })} placeholder="Defaults to you" className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Cost ($)</label><input data-testid="inv-form-cost" type="number" step="0.01" value={itemForm.cost} onChange={(e) => setItemForm({ ...itemForm, cost: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Sell price ($, tax incl.)</label><input data-testid="inv-form-price" type="number" step="0.01" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Tax %</label><input type="number" step="0.01" value={itemForm.tax_pct} onChange={(e) => setItemForm({ ...itemForm, tax_pct: e.target.value })} className={inputCls} /></div>
              <div className="min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Quantity on hand</label><input data-testid="inv-form-qty" type="number" value={itemForm.qty} onChange={(e) => setItemForm({ ...itemForm, qty: e.target.value })} className={inputCls} /></div>
              <div className="col-span-2 min-w-0"><label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Reorder threshold (alert at/below)</label><input data-testid="inv-form-threshold" type="number" value={itemForm.reorder_threshold} onChange={(e) => setItemForm({ ...itemForm, reorder_threshold: e.target.value })} className={inputCls} /></div>
            </div>
            <button data-testid="inv-form-save" type="submit" disabled={savingItem} className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{savingItem ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {itemForm.id ? "Save changes" : "Add item"}</button>
          </form>
        </div>
      )}

      {/* Stripe QR checkout modal */}
      {checkout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inv-checkout-modal">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl text-[#1e3d3a]">Take payment</h3>
              <button onClick={closeCheckout} className="p-1.5 rounded-full hover:bg-[#1e3d3a]/5"><X className="h-5 w-5" /></button>
            </div>
            {checkout.paid ? (
              <div className="py-6" data-testid="inv-checkout-paid">
                <CheckCircle2 className="h-14 w-14 text-[#2f7d5b] mx-auto" />
                <p className="mt-3 font-semibold text-[#1e3d3a]">Paid — {money(checkout.sale.total)}</p>
                <p className="text-[13px] text-[#1e3d3a]/55">{checkout.sale.item_name} · stock updated.</p>
                <button onClick={closeCheckout} className="mt-4 rounded-full bg-[#1e3d3a] px-5 py-2 text-sm font-semibold text-white">Done</button>
              </div>
            ) : (
              <>
                <p className="text-[13px] text-[#1e3d3a]/65">Have the customer scan to pay <span className="font-semibold">{money(checkout.sale.total)}</span> by card, Apple Pay or Google Pay.</p>
                <div className="my-4 flex justify-center"><div className="rounded-xl border border-[#1e3d3a]/10 p-3 bg-white"><QRCodeSVG value={checkout.url} size={196} fgColor="#1e3d3a" /></div></div>
                <a data-testid="inv-checkout-link" href={checkout.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#a3823f] hover:underline">Open checkout on this device →</a>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-[#1e3d3a]/45"><Loader2 className="h-3 w-3 animate-spin" /> Waiting for payment…</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
